/**
 * Contexto tecnico que acompaña a un ticket de soporte.
 *
 * QUE SE RECOGE Y POR QUE: lo necesario para reproducir el problema sin tener
 * que interrogar al usuario. Quien reporta, desde que taller, en que pantalla
 * exacta estaba y con que navegador. Con eso se reproduce casi cualquier falla.
 *
 * QUE NO SE RECOGE, A PROPOSITO:
 *  - El token de sesion ni nada que sirva para suplantar a alguien.
 *  - Datos de los CLIENTES del taller (nombres, telefonos, placas). El ticket
 *    lo lee ConsolTech, que no tiene por que ver la cartera de su cliente.
 *  - El contenido de la pantalla. Solo la RUTA, y con los identificadores
 *    reemplazados por `:id` (ver `describirPantalla`).
 */

/** Version de la aplicacion, inyectada por Vite en el build. */
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'desconocida';

const PANTALLAS = {
  '/dashboard': 'Panel',
  '/customers': 'Clientes',
  '/vehicles': 'Vehículos',
  '/workorders': 'Órdenes de trabajo',
  '/quotes': 'Cotizaciones',
  '/inventory': 'Inventario',
  '/services': 'Servicios',
  '/inspections': 'Inspecciones',
  '/reminders': 'Recordatorios',
  '/ratings': 'Valoraciones',
  '/loyalty': 'Regalías',
  '/shipments': 'Envíos',
  '/follow-ups': 'Seguimiento',
  '/calendar': 'Calendario',
  '/workload': 'Carga de trabajo',
  '/parts-report': 'Repuestos',
  '/users': 'Usuarios',
  '/settings': 'Configuración',
  '/shops': 'Talleres',
  '/leads': 'Leads',
  '/online-users': 'Usuarios en línea',
};

/**
 * Nombre legible de la pantalla + la ruta con los ids enmascarados.
 * `/workorders/652f...` se reporta como `/workorders/:id`: alcanza para saber
 * donde estaba sin dejar escrito a que orden concreta corresponde.
 */
export const describirPantalla = (pathname = '') => {
  const rutaBase = '/' + (pathname.split('/')[1] || '');
  const nombre = PANTALLAS[rutaBase] || rutaBase || 'Inicio';
  const rutaEnmascarada = pathname
    .replace(/\/[0-9a-f]{24}(?=\/|$)/gi, '/:id')
    .replace(/\/\d+(?=\/|$)/g, '/:id');
  const detalle = rutaEnmascarada !== rutaBase && rutaEnmascarada.includes(':id');
  return { nombre: detalle ? `${nombre} · detalle` : nombre, ruta: rutaEnmascarada };
};

/** Navegador y sistema operativo, derivados del user agent. */
export const describirNavegador = (ua = navigator.userAgent) => {
  // El orden importa: Edge y Opera tambien dicen "Chrome" en su user agent,
  // asi que si se pregunta por Chrome primero, nunca se detectan.
  const navegadores = [
    [/Edg\//, 'Edge'],
    [/OPR\//, 'Opera'],
    [/Chrome\//, 'Chrome'],
    [/Safari\//, 'Safari'],
    [/Firefox\//, 'Firefox'],
  ];
  const sistemas = [
    [/Windows NT 10/, 'Windows 10/11'],
    [/Windows/, 'Windows'],
    [/Android/, 'Android'],
    [/iPhone|iPad|iPod/, 'iOS'],
    [/Mac OS X/, 'macOS'],
    [/Linux/, 'Linux'],
  ];
  const buscar = (lista) => lista.find(([re]) => re.test(ua))?.[1] || 'desconocido';

  const esMovil = /Android|iPhone|iPod/.test(ua);
  const esTablet = /iPad|Tablet/.test(ua);
  return {
    navegador: buscar(navegadores),
    sistema: buscar(sistemas),
    tipo: esTablet ? 'tablet' : esMovil ? 'móvil' : 'escritorio',
  };
};

/**
 * Arma el contexto completo del ticket.
 * Todo es opcional: si algo falta, el ticket se envia igual.
 */
export const construirContexto = ({ user, settings, pathname } = {}) => {
  const pantalla = describirPantalla(pathname || window.location.pathname);
  const equipo = describirNavegador();

  return {
    sistema: 'Talleres (DVI Workshop)',
    version: APP_VERSION,

    taller: {
      nombre: settings?.name || null,
      codigo: settings?.code || null,
      rubro: settings?.shopType === 'motorcycles' ? 'motocicletas' : 'vehículos',
    },

    usuario: {
      nombre: user?.name || null,
      correo: user?.email || null,
      rol: user?.role || null,
    },

    pantalla: pantalla.nombre,
    ruta: pantalla.ruta,
    urlCompleta: `${window.location.origin}${pantalla.ruta}`,

    navegador: equipo.navegador,
    sistemaOperativo: equipo.sistema,
    tipoEquipo: equipo.tipo,
    pantallaTamano: `${window.innerWidth}x${window.innerHeight}`,
    idioma: navigator.language,
    zonaHoraria: Intl.DateTimeFormat().resolvedOptions().timeZone,
    enLinea: navigator.onLine,
    reportadoEn: new Date().toISOString(),
  };
};

/** Version en texto, para pegar en la descripcion del ticket. */
export const contextoComoTexto = (ctx) =>
  [
    `Sistema: ${ctx.sistema} (v${ctx.version})`,
    `Taller: ${ctx.taller.nombre || '—'} (${ctx.taller.codigo || '—'})`,
    `Usuario: ${ctx.usuario.nombre || '—'} <${ctx.usuario.correo || '—'}> · ${ctx.usuario.rol || '—'}`,
    `Pantalla: ${ctx.pantalla} — ${ctx.ruta}`,
    `Equipo: ${ctx.navegador} en ${ctx.sistemaOperativo} (${ctx.tipoEquipo}, ${ctx.pantallaTamano})`,
    `Zona horaria: ${ctx.zonaHoraria} · Reportado: ${ctx.reportadoEn}`,
  ].join('\n');

export default { construirContexto, contextoComoTexto, describirPantalla, describirNavegador };
